// Reusable skeleton loaders for list/table pages — drop-in replacement for the
// old CircularProgress / "Loading…" text while data is fetching.
//
// Usage:
//   {loading && <TableSkeleton rows={pageSize} columns={6} />}
//
// It renders glass rows that mimic a data table so the layout doesn't jump when
// the real rows arrive.
import { Box, Skeleton } from "@mui/material";
import { useThemeColors } from "../context/ThemeContext";

export function TableSkeleton({ rows = 8, columns = 5, showAvatar = true }) {
  const colors = useThemeColors();
  return (
    <Box sx={{ width: "100%" }}>
      {Array.from({ length: rows }).map((_, r) => (
        <Box
          key={r}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${colors.divider}`,
            "&:last-child": { borderBottom: "none" },
            opacity: 0,
            animation: "skelIn 0.3s ease forwards",
            animationDelay: `${Math.min(r, 10) * 0.04}s`,
            "@keyframes skelIn": { to: { opacity: 1 } },
          }}
        >
          {showAvatar && (
            <Skeleton variant="rounded" width={38} height={38} sx={{ flexShrink: 0 }} />
          )}
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton
              key={c}
              variant="text"
              height={18}
              sx={{
                // First column wider (the "name" cell), the rest even.
                flex: c === 0 ? 2 : 1,
                borderRadius: 1,
              }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

// A simple block of stacked card/line skeletons for non-table panels.
export function ListSkeleton({ rows = 5, height = 56 }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={height} />
      ))}
    </Box>
  );
}

export default TableSkeleton;
